import {
  AppHeaderBackButton,
  Box,
  ThemeProvider,
  Typography,
  Alert,
  AlertTypography,
  Icon,
  Link,
} from '@ace-clublabs/shared-component-library';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNames, ClubHelper, urlConstructor } from '@ace-clublabs/payments-helper';
import { mapMembershipLevelForTagging } from '@lib/shared';
import PaymentCard from '../components/PaymentCard';
import { Pages } from '../shared/pages';
import { useGlobalState } from '../shared/state';
import { UtagHelper } from '../utils/utagHelper';
import { AppFlow, MembershipLevel } from '@lib/types';
import { CLUB_PHONE_NUMBERS, ClubCodes } from '@ace-clublabs/payment-types';

const ReviewPage: React.FC = () => {
  const [amountDue] = useGlobalState('amountDue');
  const [autoPay] = useGlobalState('autoPay');
  const [billPlan] = useGlobalState('billPlan');
  const [, setOptedDiscount] = useGlobalState('optedDiscount');
  const [membershipLevel] = useGlobalState('membershipLevel');
  const [campaignCode] = useGlobalState('campaignCode');
  const [, setAutoPayEnroll] = useGlobalState('autoPayEnrolled');
  const navigate = useNavigate();
  const onClickBack = () => navigate(-1);
  const { discount, eligible } = autoPay || {};
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [taxAmount] = useGlobalState('taxAmount');
  const [preDiscountAmountDue] = useGlobalState('preDiscountAmountDue');
  const [productCost] = useGlobalState('productCost');
  const [appFlow] = useGlobalState('appFlow');
  const [clubCode] = useGlobalState('clubCode');
  const [tagging, setIsTagging] = useState(true);
  const [membershipProductRV] = useGlobalState('membershipProductRV');

  const handlePaymentContinue = (optedAutopay: boolean) => {
    setAutoPayEnroll(optedAutopay);
    if (eligible) {
      const displayOptedDiscount = !!discount && optedAutopay;
      setOptedDiscount(displayOptedDiscount);
    }
    eligible && optedAutopay ? navigate(Pages.AutoPay) : navigate(Pages.UpdateEmails);
  };

  useEffect(() => {
    setAutoPayEnroll(false);
    const club = ClubHelper.getClubAcronym(clubCode as ClubCodes);
    const mem_autopaycampaign = eligible && discount ? 'autopay required' : 'autopay not required';
    const mem_level = mapMembershipLevelForTagging(membershipLevel);

    const tags =
      appFlow === AppFlow.AUTOPAY
        ? {}
        : {
            mem_autopaycampaign,
            bill_status: amountDue > 0 ? 'due' : 'not due',
            campaigncodemr: campaignCode,
            mem_autopay: `${eligible}`,
          };
    UtagHelper.tag({
      ...UtagHelper.CommonTaggingValues(appFlow),
      ...tags,
      club,
      page: UtagHelper.Pages(appFlow).reviewPage,
      mem_level,
    });

    setIsTagging(false);
  }, []);

  const isTXorNM: boolean = clubCode === ClubCodes.TX || clubCode === ClubCodes.NM;
  const isPlusOrPremier: boolean =
    membershipLevel === MembershipLevel.Plus || membershipLevel === MembershipLevel.Premier;
  const isADBillPlan: boolean = billPlan === 'AD';
  const isAMBillPlan: boolean = billPlan === 'AM';

  const displayRoadsideAssistanceAlert: boolean = isTXorNM && isPlusOrPremier && (isADBillPlan || isAMBillPlan);

  const getMembershipUrl = () => {
    return urlConstructor(AppNames.MANAGE_MEMBERSHIP);
  };

  const clubPhoneNumber = CLUB_PHONE_NUMBERS[clubCode];
  const ctaText = isAMBillPlan ? (
    <>
      You can add it as a separate benefit in{' '}
      <Link
        className="secondary"
        id="terms-and-conditions-link"
        variant="b2Regular"
        target="_blank"
        href={getMembershipUrl()}
      >
        manage membership
      </Link>
      .
    </>
  ) : isMobileApp ? (
    <>
      <Link href={`tel:${clubPhoneNumber}`}>Call us</Link> to add it as a separate benefit.
    </>
  ) : (
    <>
      Call <Link href={`tel:${clubPhoneNumber}`}>{clubPhoneNumber}</Link> to add it as a separate benefit.
    </>
  );

  const alertText = (
    <AlertTypography className="sorry-text">
      <strong>Roadside Assistance for RV/motorcycles</strong> is no longer included with Plus
      <sup style={{ fontSize: '0.7em', verticalAlign: 'super', lineHeight: 0 }}>®</sup> and Premier
      <sup style={{ fontSize: '0.7em', verticalAlign: 'super', lineHeight: 0 }}>®</sup> memberships. {ctaText}
    </AlertTypography>
  );

  const pageHeader =
    appFlow === AppFlow.AUTOPAY
      ? "To enroll in Auto Pay, you'll first need to pay the minimum amount due on your account"
      : "Let's review your payment";

  if (tagging) {
    return <></>;
  }

  return (
    <>
      <ThemeProvider>
        {!isMobileApp && <AppHeaderBackButton id="back-button" onClick={onClickBack} />}
        <Box id="payment-review-form">
          <Typography variant="h5Medium" component="h1" id="payment-review-header" sx={{ mt: 2 }}>
            {pageHeader}
          </Typography>
          {displayRoadsideAssistanceAlert && (
            <Alert
              id="roadside-alert"
              severity="info"
              displayCloseButton={false}
              icon={<Icon iconName="bullhorn-regular" />}
              sx={{ mt: 2 }}
            >
              {alertText}
            </Alert>
          )}
          <Box className="payment-cards">
            <PaymentCard
              onClickWithAutoPay={() => handlePaymentContinue(true)}
              amountDue={amountDue}
              membershipLevel={membershipLevel}
              preDiscountAmountDue={preDiscountAmountDue}
              productCost={productCost}
              taxAmount={taxAmount}
              eligible={eligible}
              discount={discount && discount > 0 ? discount : undefined}
              onClickWithoutAutoPay={() => handlePaymentContinue(false)}
              membershipProductRV={membershipProductRV}
            />
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );
};

export default ReviewPage;
export { ReviewPage };
